import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  StepFormContainer,
  StepOneOrganizerSelection,
  StepTwoEventInformation,
  StepThreeEventTickets,
  StepFourReviewEvent,
} from "./components";
import {
  transformEventPayload,
  transformEventToFormData,
} from "./utils/payloadTransformer";
import eventService from "../../../../api/EventService";
import organizerService from "../../../../api/OrganizerService";

const EditEvent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    organizer: null,
    // Step 2 data
    eventType: [],
    eventStartDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    venueName: "",
    venueStreetAddress: "",
    country: "",
    city: "",
    venueGoogleLocation: "",
    thumbnail: null,
    featuredImages: [],
    aboutEvent: "",
    performers: [],
    sponsors: [],
    agenda: [],
    // Step 3 data
    overallSlots: "",
    ticketTypes: [],
  });

  const totalSteps = 4;
  const stepTitles = [
    "Select Organizer",
    "Event Information",
    "Event Tickets",
    "Review & Submit",
  ];

  // Fetch event data
  useEffect(() => {
    const fetchEventData = async () => {
      // If event data was passed via state, use it
      if (location.state?.eventData) {
        const eventData = location.state.eventData;

        // Fetch organizer data if organizer_slug exists
        let organizer = null;
        if (eventData.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              organizer = organizersList.find(
                (org) => org.slug === eventData.organizer_slug
              );
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }

        // Transform backend data to frontend format
        const transformedData = transformEventToFormData(eventData, organizer);
        console.log("TRANSFORMED DATA: ", transformedData);

        if (transformedData) {
          setFormData(transformedData);
        } else {
          setError("Failed to transform event data");
          toast.error("Failed to transform event data");
        }
        setIsLoading(false);
        return;
      }

      // Otherwise, fetch from API (fallback for direct navigation)
      if (!event_slug) {
        setError("Event identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch event data
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(eventResponse.message || "Failed to fetch event");
        }

        // Handle different response structures
        let eventData = null;
        if (eventResponse.data.status_code === 200 && eventResponse.data.data) {
          eventData = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          eventData = eventResponse.data;
        }

        if (!eventData) {
          throw new Error("Event data not found");
        }

        // Fetch organizer data if organizer_slug exists
        let organizer = null;
        if (eventData.organizer_slug) {
          try {
            const organizersResponse = await organizerService.getOrganizers();
            if (organizersResponse.data?.status_code === 200) {
              const organizersList = Array.isArray(organizersResponse.data.data)
                ? organizersResponse.data.data
                : organizersResponse.data.data || [];
              organizer = organizersList.find(
                (org) => org.slug === eventData.organizer_slug
              );
            }
          } catch (orgError) {
            console.warn("Could not fetch organizer:", orgError);
          }
        }

        // Transform backend data to frontend format
        const transformedData = transformEventToFormData(eventData, organizer);

        if (transformedData) {
          setFormData(transformedData);
        } else {
          throw new Error("Failed to transform event data");
        }
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading event data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  const handleOrganizerSelect = (organizer) => {
    setFormData((prev) => ({
      ...prev,
      organizer,
    }));
  };

  const handleStepOneNext = () => {
    if (formData.organizer) {
      setCurrentStep(2);
    } else {
      toast.error("Please select an organizer");
    }
  };

  const handleStepTwoBack = () => {
    setCurrentStep(1);
  };

  const handleStepTwoNext = () => {
    setCurrentStep(3);
  };

  const handleStepThreeBack = () => {
    setCurrentStep(2);
  };

  const handleStepThreeNext = () => {
    setCurrentStep(4);
  };

  const handleStepFourBack = () => {
    setCurrentStep(3);
  };

  const handleSubmitEvent = async (data) => {
    if (!event_slug) {
      toast.error("Event identifier is missing");
      return;
    }

    const payload = transformEventPayload(data);

    if (!payload.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }

    try {
      setIsLoading(true);
      const response = await eventService.updateEvent(event_slug, payload);

      if (response.status && response.data?.status_code === 200) {
        toast.success(
          response.data.reason ||
            response.message ||
            "Event updated successfully!"
        );
        navigate("/admin/media/events");
      } else {
        toast.error(
          response.data.reason || response.message || "Failed to update event"
        );
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.reason ||
        err.response?.data?.message ||
        err.message ||
        "An error occurred while updating event";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataUpdate = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-spin" />
          <p className="text-gray-600">Loading event data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Events</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Event
          </h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={() => navigate("/admin/media/events")}
            className="px-6 py-2 text-white bg-purple-600 rounded-lg transition-colors hover:bg-purple-700"
          >
            Go Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center mb-3 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Events</span>
        </button>

        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Edit Event
            </h1>
            <p className="mt-0.5 text-xs text-gray-600">
              Update event details and information
            </p>
          </div>
        </div>
      </div>

      <StepFormContainer
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitles={stepTitles}
        showProgress={true}
      >
        {/* Step Content */}
        {currentStep === 1 && (
          <StepOneOrganizerSelection
            selectedOrganizer={formData.organizer}
            onSelect={handleOrganizerSelect}
            onNext={handleStepOneNext}
          />
        )}

        {currentStep === 2 && (
          <StepTwoEventInformation
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepTwoBack}
            onNext={handleStepTwoNext}
          />
        )}

        {currentStep === 3 && (
          <StepThreeEventTickets
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepThreeBack}
            onNext={handleStepThreeNext}
          />
        )}

        {currentStep === 4 && (
          <StepFourReviewEvent
            formData={formData}
            onBack={handleStepFourBack}
            onSubmit={handleSubmitEvent}
          />
        )}
      </StepFormContainer>
    </>
  );
};

export default EditEvent;
